// Frontend/src/components/PaymentDetailsCard.tsx
/**
 * Reusable component to display payment details for platform or HOA admin
 * Used in invoice detail pages and payment flow screens
 */

import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  CreditCard,
  Mail,
  Phone,
  User,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import type { PaymentDetails } from "@/api/payments";
import { useLanguage } from "@/contexts/LanguageContext";

interface PaymentDetailsCardProps {
  details: PaymentDetails | null;
  title: string;
  description?: string;
  showVerificationBadge?: boolean;
  className?: string;
}

export function PaymentDetailsCard({
  details,
  title,
  description,
  showVerificationBadge = true,
  className = "",
}: PaymentDetailsCardProps) {
  const { t } = useLanguage();
  if (!details) {
    return (
      <Card className={`rounded-2xl p-6 border-dashed ${className}`}>
        <div className="text-center text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">{t("payment.details.notAvailable", { title })}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`rounded-2xl p-6 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
        {showVerificationBadge && (
          <Badge
            variant={details.is_verified ? "default" : "secondary"}
            className="rounded-lg"
          >
            {details.is_verified ? (
              <>
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {t("payment.details.verified")}
              </>
            ) : (
              <>
                <AlertCircle className="w-3 h-3 mr-1" />
                {t("payment.details.unverified")}
              </>
            )}
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        {details.user_email && (
          <div className="flex items-start gap-3">
            <User className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.accountEmail")}</p>
              <p className="text-sm font-medium text-gray-900 break-all">
                {details.user_email}
              </p>
            </div>
          </div>
        )}

        {details.bank_name && (
          <div className="flex items-start gap-3">
            <Building2 className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.bankTransfer")}</p>
              <p className="text-sm font-medium text-gray-900">
                {details.bank_name}
              </p>
              {details.account_holder_name && (
                <p className="text-xs text-gray-600 mt-1">
                  {details.account_holder_name}
                </p>
              )}
              {details.bank_account_number && (
                <p className="text-xs text-gray-600 font-mono">
                  {t("payment.details.account", { number: details.bank_account_number })}
                </p>
              )}
              {details.bank_routing_number && (
                <p className="text-xs text-gray-600 font-mono">
                  {t("payment.details.routing", { number: details.bank_routing_number })}
                </p>
              )}
            </div>
          </div>
        )}

        {details.zelle_email && (
          <div className="flex items-start gap-3">
            <CreditCard className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.zelle")}</p>
              <p className="text-sm font-medium text-gray-900 break-all">
                {details.zelle_email}
              </p>
            </div>
          </div>
        )}

        {details.zelle_phone && !details.zelle_email && (
          <div className="flex items-start gap-3">
            <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.zellePhone")}</p>
              <p className="text-sm font-medium text-gray-900">
                {details.zelle_phone}
              </p>
            </div>
          </div>
        )}

        {details.paypal_email && (
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.paypal")}</p>
              <p className="text-sm font-medium text-gray-900 break-all">
                {details.paypal_email}
              </p>
            </div>
          </div>
        )}

        {details.venmo_username && (
          <div className="flex items-start gap-3">
            <CreditCard className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.venmo")}</p>
              <p className="text-sm font-medium text-gray-900">
                {details.venmo_username}
              </p>
            </div>
          </div>
        )}

        {details.cashapp_username && (
          <div className="flex items-start gap-3">
            <CreditCard className="w-4 h-4 text-gray-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">{t("payment.details.cashapp")}</p>
              <p className="text-sm font-medium text-gray-900">
                {details.cashapp_username}
              </p>
            </div>
          </div>
        )}

        {!details.bank_name &&
          !details.zelle_email &&
          !details.zelle_phone &&
          !details.paypal_email &&
          !details.venmo_username &&
          !details.cashapp_username && (
            <p className="text-sm text-gray-500 italic">
              {t("payment.details.noMethods")}
            </p>
          )}
      </div>
    </Card>
  );
}
